import { useState } from "react";
import { TellescopeModelFromName, useTellescopeMutation } from "@tellescope/tanstack-query";
import { ConfirmDialog } from "@tellescope/ui";
import { useTranslation } from "react-i18next";
import { ContentCard } from "./ContentCard";
import { BlockPaginatedList } from "../BlockPaginatedList";
import { BlockEmptyState } from "../BlockEmptyState";
import { useContentRecords } from "./useContentRecords";

export interface ContentRecordsListProps {
  contact: TellescopeModelFromName<"endusers">;
}

export const ContentRecordsList = ({ contact }: ContentRecordsListProps) => {
  const { t } = useTranslation("contacts");
  const [contentAssignmentToDelete, setContentAssignmentToDelete] = useState<string | null>(null);

  const { assignedContent, isLoading, fetchNextPage } = useContentRecords(contact.id);

  const deleteContentAssignmentMutation = useTellescopeMutation({
    model: "managed_content_record_assignments",
    method: "deleteOne",
    onSuccess: () => {
      setContentAssignmentToDelete(null);
    },
  });

  return (
    <div className="flex flex-1 flex-col space-y-2 pr-2">
      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <BlockEmptyState>{t("Loading content...")}</BlockEmptyState>
        </div>
      ) : assignedContent.length ? (
        <>
          <BlockPaginatedList className="flex flex-col" scrollbar="floating" onBottomReached={fetchNextPage}>
            {assignedContent.map(({ content, assignment }) => (
              <ContentCard
                key={content.id}
                title={content.title}
                type={content.type}
                deleteLabel={t("Delete")}
                onDelete={() => setContentAssignmentToDelete(assignment.id)}
                className="mb-2"
              />
            ))}
          </BlockPaginatedList>

          <ConfirmDialog
            open={!!contentAssignmentToDelete}
            onOpenChange={(open) => !open && setContentAssignmentToDelete(null)}
            variant="destructive"
            title={t("Delete Content Assignment")}
            description={t("Are you sure you want to delete this content assignment? This action cannot be undone.")}
            confirmText={t("Delete")}
            onConfirm={async () => {
              if (contentAssignmentToDelete) {
                await deleteContentAssignmentMutation.mutateAsync(contentAssignmentToDelete);
              }
            }}
          />
        </>
      ) : (
        <div className="flex flex-1 items-center justify-center">
          <BlockEmptyState>{t("No content for this contact.")}</BlockEmptyState>
        </div>
      )}
    </div>
  );
};
